import React, {useEffect, useState} from "react";
import {useObserver} from "mobx-react-lite";
import {useStore} from "../../hooks/useStore";
import {useFetch} from "../../hooks/useFetch";
import {Col, List, Row, Skeleton, Card, Tabs} from "antd";
import {LockOutlined, AuditOutlined, ShopOutlined} from "@ant-design/icons";
import {useTranslation} from "react-i18next";

import {HeaderText, PasswordCard, StyledCard, StyledTabs, ProfileContainer} from "./profileStyles";
import {ChangePasswordForm} from "./ChangePasswordForm/ChangePasswordForm";
import CommunicationPreferencesCard from "../_defaultComponents/CommunicationPreferencesCard/CommunicationPreferencesCard";
import {BillingInformationForm} from "./BillingInformationForm/BillingInformationForm";
import {AuthorizedIDForm} from "./AuthorizedIDForm/AuthorizedIDForm";
import {PaymentInfoForm} from "./PaymentInfoForm/PaymentInfoForm";
import {UserInfoCard} from "./UserInfoCard/UserInfoCard";
import {CustomerInfoCard} from "./CustomerInfoCard/CustomerInfoCard";
import {OnboardingCompanyCard} from "./OnboardingCompanyCard/OnboardingCompanyCard";

export const ProfilePage = () => {
    const stores = useStore();
    const {t} = useTranslation(["profilePage"]);
    const {username, email, company, role} = stores.meStore;
    const [userInfo, setUserInfo] = useState([
        {title: t("fields.username", {ns: "common"}), value: username},
        {title: "E-mail", value: email}
    ]);
    const isCustomer = stores?.meStore?.checkRole("isCompanyUser");
    const companyName = stores?.meStore?.companies[0]?.companyName;
    const [loading, userProfileInfo, setuserProfileInfo, setLoading, refetch] = useFetch({
        fetchFn: stores.userStore.getUserInfo.bind(null, {})
    });
    const selectedSubCompanyId = stores.meStore.selectedSubCompanyId;
    const selectedSubCompanyName = userProfileInfo?.subcompanyInfos?.find(
        subCompany => subCompany._id === selectedSubCompanyId
    )?.name;
    const [activeTab, setActiveTab] = useState("1");

    useEffect(() => {
        if (
            [
                "cUser",
                "oUser",
                "sOperation",
                "sChef",
                "fieldManager",
                "bm",
                "agency",
                "sy"
            ].includes(role)
        ) {
            const userCompanyInfos = userProfileInfo?.companyInfos;
            const userSubCompanyInfos = userProfileInfo?.subcompanyInfos;

            const newUserInfo = [
                {title: t("fields.username", {ns: "common"}), value: username},
                {title: "E-mail", value: email}
            ];

            if (userCompanyInfos?.length > 0) {
                const companies = userCompanyInfos.map(company => company.companyName).join(", ");
                newUserInfo.push({
                    title: t("fields.companyName", {ns: "common"}),
                    value: companies || t("profilePage.notCompanyAssigned", {ns: "profilePage"})
                });
            }

            if (userSubCompanyInfos?.length > 0) {
                const subCompanies = userSubCompanyInfos
                    .map(subCompany => subCompany.name)
                    .join(", ");
                newUserInfo.push({
                    title: t("fields.subcompanyName", {ns: "common"}),
                    value: subCompanies || t("profilePage.notCompanyAssigned", {ns: "profilePage"})
                });
            }

            setUserInfo(newUserInfo);
        }
    }, [userProfileInfo]);

    return useObserver(() => {
        const selectedSubCompanyId = stores.meStore.selectedSubCompanyId;
        const selectedSubCompany = userProfileInfo?.subcompanyInfos?.find(
            subCompany => subCompany._id === selectedSubCompanyId
        );

        // Loading durumunda skeleton göster
        if (loading) {
            return (
                <Row
                    type="flex"
                    justify="center"
                    data-testid="profileScreen"
                    style={{width: "100%"}}
                >
                    <Col md={24} sm={22}>
                        <Skeleton active paragraph={{rows: 10}} />
                    </Col>
                </Row>
            );
        }

        return (
            <Row type="flex" justify="center" data-testid="profileScreen" style={{width: "100%"}}>
                <Col md={24} sm={22}>
                    {!stores?.meStore?.checkRole("isCompanyUser") ? (
                        <ProfileContainer>
                            <Row gutter={[16, 16]} style={{marginBottom: "20px"}}>
                                <Col md={12} sm={24}>
                                    <UserInfoCard username={username} email={email} />
                                </Col>
                                <Col md={12} sm={24}>
                                    <PasswordCard
                                        title={
                                            <h4>
                                                <LockOutlined />
                                                <HeaderText>
                                                    {t("profilePage.actions.changePassword", {
                                                        ns: "profilePage"
                                                    })}
                                                </HeaderText>
                                            </h4>
                                        }
                                    >
                                        <ChangePasswordForm isCustomer={isCustomer} />
                                    </PasswordCard>
                                </Col>
                            </Row>
                            <Row gutter={[16, 16]} style={{marginBottom: "20px"}}>
                                <Col md={12} sm={24}>
                                    <CommunicationPreferencesCard />
                                </Col>
                            </Row>
                        </ProfileContainer>
                    ) : (
                        <>
                            {!userProfileInfo ? (
                                <StyledCard>
                                    <div style={{textAlign: "center", padding: "20px"}}>
                                        <p>
                                            Profil bilgileri yüklenemedi. Lütfen sayfayı yenileyin.
                                        </p>
                                    </div>
                                </StyledCard>
                            ) : (
                                <>
                                    <StyledTabs activeKey={activeTab} onChange={setActiveTab}>
                                        <Tabs.TabPane
                                            tab={
                                                <span>
                                                    <span
                                                        style={{
                                                            fontWeight: "bold",
                                                            fontSize: "16px"
                                                        }}
                                                    >
                                                        {companyName}
                                                    </span>
                                                    {" Firma Bilgileri"}
                                                </span>
                                            }
                                            key="1"
                                        >
                                            <ProfileContainer>
                                                <Row
                                                    gutter={[16, 16]}
                                                    style={{marginBottom: "20px"}}
                                                >
                                                    <Col md={24} sm={24}>
                                                        <OnboardingCompanyCard />
                                                    </Col>
                                                </Row>
                                                <Row
                                                    gutter={[16, 16]}
                                                    style={{marginBottom: "20px"}}
                                                >
                                                    <Col md={12} sm={24}>
                                                        <UserInfoCard
                                                            username={username}
                                                            email={email}
                                                        />
                                                    </Col>
                                                    <Col md={12} sm={24}>
                                                        <PasswordCard
                                                            title={
                                                                <h4>
                                                                    <LockOutlined />
                                                                    <HeaderText>
                                                                        {t(
                                                                            "profilePage.actions.changePassword",
                                                                            {
                                                                                ns: "profilePage"
                                                                            }
                                                                        )}
                                                                    </HeaderText>
                                                                </h4>
                                                            }
                                                        >
                                                            <ChangePasswordForm
                                                                isCustomer={isCustomer}
                                                            />
                                                        </PasswordCard>
                                                    </Col>
                                                </Row>
                                                <Row
                                                    gutter={[16, 16]}
                                                    style={{marginBottom: "20px"}}
                                                >
                                                    <Col md={12} sm={24}>
                                                        <CommunicationPreferencesCard />
                                                    </Col>
                                                </Row>
                                            </ProfileContainer>
                                        </Tabs.TabPane>
                                        <Tabs.TabPane
                                            tab={
                                                <span>
                                                    <span
                                                        style={{
                                                            fontWeight: "bold",
                                                            fontSize: "16px"
                                                        }}
                                                    >
                                                        {selectedSubCompanyName}
                                                    </span>
                                                    {" Alt Firma Bilgileri"}
                                                </span>
                                            }
                                            key="2"
                                        >
                                            <Row gutter={[16, 16]} style={{marginBottom: "20px"}}>
                                                <Col md={12} sm={24}>
                                                    <CustomerInfoCard
                                                        userProfileInfo={userProfileInfo}
                                                    />
                                                </Col>
                                                {isCustomer && selectedSubCompany && (
                                                    <Col md={12} sm={24}>
                                                        <AuthorizedIDForm
                                                            subCompany={selectedSubCompany}
                                                            loading={loading}
                                                            refetch={refetch}
                                                            isCustomer={isCustomer}
                                                        />
                                                    </Col>
                                                )}
                                            </Row>
                                            {isCustomer && selectedSubCompany && (
                                                <Row gutter={[16, 16]}>
                                                    <Col md={24} sm={24}>
                                                        <BillingInformationForm
                                                            subCompany={selectedSubCompany}
                                                            loading={loading}
                                                            refetch={refetch}
                                                            isCustomer={isCustomer}
                                                        />
                                                    </Col>
                                                </Row>
                                            )}
                                            {isCustomer && selectedSubCompany && (
                                                <Row
                                                    gutter={[16, 16]}
                                                    style={{marginBottom: "20px"}}
                                                >
                                                    <Col md={12} sm={24}>
                                                        <PaymentInfoForm
                                                            subCompany={selectedSubCompany}
                                                            loading={loading}
                                                            refetch={refetch}
                                                            isCustomer={isCustomer}
                                                        />
                                                    </Col>
                                                </Row>
                                            )}
                                        </Tabs.TabPane>
                                    </StyledTabs>
                                </>
                            )}
                        </>
                    )}
                </Col>
            </Row>
        );
    });
};

const styles = {
    list: {
        backgroundColor: "#fff",
        borderRadius: "4px"
    },
    listItem: {
        padding: "12px 0",
        borderBottom: "1px solid #f0f0f0"
    },
    listItemMeta: {
        padding: "10px 20px",
        minWidth: "150px",
        fontWeight: "500",
        color: "#666"
    },
    listItemValue: {
        padding: "10px 20px",
        flex: 1,
        fontWeight: "400",
        color: "#333"
    }
};
